import { Form, Formik } from 'formik';
import * as Yup from 'Utils/yup';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { TextField } from 'Controls/Fields/TextField';
import { DropdownField } from 'Controls/Fields/DropdownField';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { TestContext } from 'yup/lib/util/createValidation';
import { getQuestionFieldBuilders } from '../Questionnaries';
import {
    AssessmentsAssessment,
    AssessmentsLmvaDetails,
    QuestionnairesQuestionnaire,
    QuestionnairesQuestionResponse as QuestionResponse,
} from '@projectcanary/trustwell-server-client-ts';
import { withInitializers } from 'Utils/withInitializers';
import { assessmentApi } from 'Services/TrustwellApiService';
import { FormikProps } from 'formik/dist/types';
import HardwareIcon from '@mui/icons-material/Hardware';

export type EditLmvaFormLmva = Partial<AssessmentsLmvaDetails>;

type FormViewModel = Partial<Omit<AssessmentsLmvaDetails, 'responses'>> & { autoGeneratedName: string; responses: Record<string, any> };

const vintageOptions = (() => {
    const yearToday = new Date().getFullYear();
    const yearOptions = _.range(yearToday - 4, yearToday + 1);
    return yearOptions;
})();

export const emissionProtocols = {
    USA: ['US EPA'],
    CAN: ['WCI'],
};

export const methaneIntensityProtocols = {
    USA: ['OGCI', 'OneFuture', 'NGSI', 'Low Methane Rating'],
    CAN: ['NGSI, modified for Canadian regulatory reporting', 'Low Methane Rating'],
};

function generateLmvaName(values: { methaneIntensityProtocol?: string; vintage?: number; methaneIntensity?: number }) {
    return [values.methaneIntensityProtocol, values.vintage, values.methaneIntensity && Number(values.methaneIntensity).toFixed(4) + '%']
        .filter(Boolean)
        .join(' - ');
}

const staticFieldsValidationSchema = {
    name: Yup.string().required('LMVA name is required.').noEnclosingSpaces(),
    methaneIntensity: Yup.number()
        .positive('Methane intensity must be a positive number.')
        .test(
            'methane-intensity',
            "Methane intensity must have exactly 4 decimal places and be in this format: '0.####'.",
            function (value: number | undefined, context: TestContext) {
                // HACK: yup doesn't have original value available in the *type* of context until 1.0.0-beta.7
                // but the value is actually there (just not documented).
                // once 1.0 is out of beta - we can remove this hack (i.e. just do context.originalValue).
                const originalValue = (context as any as { originalValue: string | undefined }).originalValue;
                if (originalValue !== undefined) {
                    return /^\d+\.\d{4}$/.test(originalValue);
                }
                return true;
            }
        )
        .max(10, '10 is the maximum allowed value.')
        .required('Methane intensity is required.'),
    vintage: Yup.number().label('Vintage').required(),
    methaneIntensityProtocol: Yup.string().label('Intensity protocol').required(),
    emissionProtocol: Yup.string().label('Emission protocol').required(),
    segment: Yup.string(),
};

export type EditLmvaFormParams = {
    innerRef: React.Ref<FormikProps<any>>;
    onSubmit: (lmva: EditLmvaFormLmva) => void;
    lmva: Partial<AssessmentsLmvaDetails>;
    assessment: AssessmentsAssessment;
    existingLmvaNames: string[];
    questionnaire: QuestionnairesQuestionnaire;
};

const InnerEditLmvaForm = ({ innerRef, onSubmit, lmva, assessment, existingLmvaNames, questionnaire }: EditLmvaFormParams) => {
    const questionFields: { text: string; name: string; component: JSX.Element }[] = [];

    const initialValues: FormViewModel = _.assign(
        {
            name: '',
        },
        lmva,
        {
            autoGeneratedName: generateLmvaName(lmva),
            responses: {},
            methaneIntensity: typeof lmva.methaneIntensity !== 'undefined' ? lmva.methaneIntensity.toFixed(4) : '',
        }
    );

    const disallowedLmvaNames = _.reject(existingLmvaNames, (n) => n === lmva.name);
    const extendedNameRule = staticFieldsValidationSchema.name.notOneOf(disallowedLmvaNames, 'LMVA with this name already exists in the assessment.');

    const validationSchema: any = { ...staticFieldsValidationSchema, name: extendedNameRule };

    const responsesValidationSchema = {};
    for (const q of questionnaire.questions) {
        const { componentBuilder, validationBuilder, defaultValue } = getQuestionFieldBuilders(q);
        const fieldName = 'responses[' + String(q.questionnaireQuestionId) + ']';
        questionFields.push({
            name: fieldName,
            text: q.text,
            component: componentBuilder(fieldName),
        });

        initialValues.responses[q.questionnaireQuestionId] =
            _.find(lmva.responses, { questionnaireQuestionId: q.questionnaireQuestionId })?.answer ?? defaultValue;
        responsesValidationSchema[q.questionnaireQuestionId] = validationBuilder();
    }
    validationSchema.responses = Yup.object().shape(responsesValidationSchema);

    return (
        <Formik<FormViewModel>
            innerRef={innerRef}
            initialValues={initialValues}
            validationSchema={Yup.object().shape(validationSchema)}
            enableReinitialize
            onSubmit={(lmva) => {
                const outputModel: EditLmvaFormLmva = {
                    ...lmva,
                    responses: _.map(initialValues.responses, (v, k) => {
                        const response = lmva.responses[k];
                        return {
                            questionnaireQuestionId: Number(k),
                            answer: response,
                            isNA: false,
                            isExplicitlyOmitted: false, // HACK: using same DTO for create and get operations. need
                            // to come up with a better strategy.
                            id: 0,
                        } as QuestionResponse;
                    }),
                };
                onSubmit(outputModel);
            }}
        >
            {({ values, setFieldValue, setFieldTouched, errors }) => {
                const newAutoGeneratedName = generateLmvaName(values);

                console.log({ errors });

                function setNameToAutoGeneratedName() {
                    setFieldValue('name', newAutoGeneratedName);
                    // HACK: there is a bug in formik: setting a value and immediately setting touched
                    // doesn't work. Touched has to be done asynchronously.
                    window.setTimeout(() => {
                        setFieldTouched('name');
                    }, 0);
                    setFieldValue('autoGeneratedName', newAutoGeneratedName);
                }

                if (values.name === values.autoGeneratedName && values.autoGeneratedName !== newAutoGeneratedName) {
                    setNameToAutoGeneratedName();
                }

                return (
                    <Form>
                        <Grid container spacing={2} columns={10}>
                            <Grid item xs={5}>
                                <TextField
                                    name="name"
                                    label="LMVA Name (auto-generated)"
                                    buttons={{
                                        icon: <HardwareIcon />,
                                        title: 'Auto-generate LMVA name.',
                                        onClick: setNameToAutoGeneratedName,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextField name="methaneIntensity" label="Methane Intensity (%)" type="number" />
                            </Grid>
                            <Grid item xs={5}>
                                <DropdownField options={vintageOptions} name="vintage" label="Vintage" includeEmptyOption={false} />
                            </Grid>
                            <Grid item xs={5}>
                                <DropdownField
                                    options={methaneIntensityProtocols[assessment.countryCode]}
                                    disabled={methaneIntensityProtocols[assessment.countryCode].length === 0}
                                    name="methaneIntensityProtocol"
                                    label="Protocol (Intensity)"
                                    includeEmptyOption={false}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField name="emissionProtocol" label="Protocol (Emissions)" readonly />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField name="segment" label="Segment" readonly />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField name="production" label="Production (MSCF)" readonly />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField name="benchmark" label="Benchmark" readonly />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField name="granularity" label="Granularity" readonly />
                            </Grid>
                        </Grid>
                        <Stack>
                            <h6>Program Specification</h6>
                        </Stack>
                        <Stack>
                            {_.map(
                                questionFields, //
                                (qf) => (
                                    <Box sx={{ mb: 2 }} key={qf.name}>
                                        <Typography>{qf.text}</Typography>
                                        <Box sx={{ ml: 4 }}>{qf.component}</Box>
                                    </Box>
                                )
                            )}
                        </Stack>
                    </Form>
                );
            }}
        </Formik>
    );
};

export const EditLmvaForm = withInitializers(
    {
        async existingLmvaNames({ assessment }) {
            const api = await assessmentApi();
            return _.chain(await api.getAllLmvasByAssessment(assessment.id))
                .map('name')
                .uniq()
                .value();
        },
    },
    InnerEditLmvaForm,
    'component'
);
